import { useEffect, useRef } from "react";
import { Eye, EyeOff, Trash2, X } from "lucide-react";
import { useSvgDiagram2D } from "./SvgDiagram2DContext";
import type { ShapeStylePatch } from "./SvgDiagram2DContext";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { FillControl } from "../reusable/FillControl";
import type {
    DiagramArc,
    DiagramEllipse,
    DiagramLine,
    DiagramPolygon,
    DiagramRect,
    Fill,
} from "../../utils/svgDiagram2DModel";

/**
 * Whether the current selection spans 2+ standalone shapes (ellipses, lines,
 * arcs, rects, polygons — any mix). When true the multi-select panel takes over
 * and the per-type inspectors suppress themselves so only one panel shows.
 */
export function isMultiShapeSelection(sel: {
    selectedEllipseIds: string[];
    selectedLineIds: string[];
    selectedArcIds: string[];
    selectedRectIds: string[];
    selectedPolygonIds: string[];
}): boolean {
    return (
        sel.selectedEllipseIds.length +
            sel.selectedLineIds.length +
            sel.selectedArcIds.length +
            sel.selectedRectIds.length +
            sel.selectedPolygonIds.length >=
        2
    );
}

function num(v: string | number, fallback: number): number {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
}

/** Any standalone shape this panel can bulk-edit. */
type StyledShape =
    | DiagramEllipse
    | DiagramLine
    | DiagramArc
    | DiagramRect
    | DiagramPolygon;

/** A common value across the selection, or the `mixed` sentinel when they differ. */
const MIXED = Symbol("mixed");
type CommonOf<T> = T | typeof MIXED;

/**
 * Reduce a property across the selected shapes to either the single shared value
 * (every shape agrees) or `MIXED` (they differ). Shapes lacking the property
 * (e.g. lines/arcs have no `fill`) are skipped via `present`.
 */
function common<T>(
    shapes: StyledShape[],
    pick: (s: StyledShape) => T,
    eq: (a: T, b: T) => boolean = Object.is,
): CommonOf<T> | undefined {
    let acc: T | undefined;
    let seen = false;
    for (const s of shapes) {
        const v = pick(s);
        if (!seen) {
            acc = v;
            seen = true;
        } else if (!eq(acc as T, v)) {
            return MIXED;
        }
    }
    return seen ? (acc as T) : undefined;
}

function fillEq(a: Fill | null, b: Fill | null): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (a.type !== b.type) return false;
    if (a.type === "solid" && b.type === "solid") return a.color === b.color;
    if (a.type === "pattern" && b.type === "pattern")
        return a.patternId === b.patternId;
    return false;
}

/**
 * Floating inspector shown when 2+ standalone shapes (ellipses, lines, arcs,
 * rects, polygons — any mix) are selected together. It edits the style fields
 * they share — stroke color / width / dash / preset, fill (for the fillable
 * shapes), and visibility — applying each change to every selected shape at
 * once. A field whose selected shapes disagree shows "Mixed"; editing it sets
 * them all to the new value. Nodes are excluded (their color is a marker fill,
 * not a stroke) and are edited from the single-node panel.
 *
 * Renders alongside the per-type inspectors; those suppress themselves while a
 * multi-shape selection is active (see `isMultiShapeSelection`), so only one of
 * the two appears.
 */
export function SvgDiagram2DMultiSelectPanel() {
    const {
        doc,
        selectedEllipseIds,
        selectedLineIds,
        selectedArcIds,
        selectedRectIds,
        selectedPolygonIds,
        updateSelectedShapesStyle,
        removeSelectedEllipses,
        removeSelectedLines,
        removeSelectedArcs,
        removeSelectedRects,
        removeSelectedPolygons,
        clearSelection,
    } = useSvgDiagram2D();

    const ellSel = doc.ellipses.filter((e) => selectedEllipseIds.includes(e.id));
    const linSel = doc.lines.filter((l) => selectedLineIds.includes(l.id));
    const arcSel = doc.arcs.filter((a) => selectedArcIds.includes(a.id));
    const rctSel = doc.rects.filter((r) => selectedRectIds.includes(r.id));
    const polSel = doc.polygons.filter((p) => selectedPolygonIds.includes(p.id));

    const shapes: StyledShape[] = [
        ...ellSel,
        ...linSel,
        ...arcSel,
        ...rctSel,
        ...polSel,
    ];

    // Only render for a true multi-shape selection (2+ shapes). One shape uses
    // its own type panel; zero means nothing is selected.
    if (shapes.length < 2) return null;

    const apply = (patch: ShapeStylePatch) => updateSelectedShapesStyle(patch);

    const color = common(shapes, (s) => s.color);
    const colorMixed = color === MIXED;
    const width = common(shapes, (s) => s.strokeWidth);
    const widthMixed = width === MIXED;
    const dash = common(shapes, (s) => s.dash);
    const dashMixed = dash === MIXED;
    const styleId = common(shapes, (s) => s.strokeStyleId ?? "");
    const styleMixed = styleId === MIXED;
    // A style that exists and is shared by all overrides the plain stroke
    // fields, so they're hidden (mirrors the single-shape StrokeStyleControl).
    const plainStrokeHidden =
        !styleMixed &&
        typeof styleId === "string" &&
        styleId !== "" &&
        doc.strokeStyles.some((s) => s.id === styleId);
    const visible = common(shapes, (s) => s.visible);
    const visibleMixed = visible === MIXED;

    // Fill only applies when every selected shape is fillable (ellipse / rect /
    // polygon). A line or arc in the mix hides the fill control entirely.
    const fillable = linSel.length === 0 && arcSel.length === 0;
    const fill = fillable
        ? common(
              shapes,
              (s) => ("fill" in s ? s.fill : null),
              fillEq,
          )
        : undefined;
    const fillMixed = fill === MIXED;

    return (
        <div className="diagram-node-panel">
            <div className="field-row" style={{ alignItems: "center", gap: 6 }}>
                <strong style={{ flex: 1, minWidth: 0 }}>
                    {shapes.length} elements selected
                </strong>
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => clearSelection()}
                    title="Clear selection"
                    style={{ padding: "2px 6px" }}
                >
                    <X size={14} aria-hidden />
                </button>
            </div>

            <p
                className="path-tool-meta"
                style={{ margin: "0 0 8px", opacity: 0.7 }}
            >
                Edits apply to all selected shapes. Fields that differ show
                "Mixed".
            </p>

            {/* --- Style --- */}
            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Style</p>

                <label className="field-label">
                    Stroke style
                    <select
                        value={styleMixed ? "__mixed__" : (styleId as string)}
                        disabled={doc.strokeStyles.length === 0}
                        onChange={(e) => {
                            if (e.target.value === "__mixed__") return;
                            apply({
                                strokeStyleId:
                                    e.target.value === ""
                                        ? null
                                        : e.target.value,
                            });
                        }}
                    >
                        {styleMixed && (
                            <option value="__mixed__">— Mixed —</option>
                        )}
                        <option value="">None (plain stroke)</option>
                        {doc.strokeStyles.map((s) => (
                            <option key={s.id} value={s.id}>
                                {s.name}
                            </option>
                        ))}
                    </select>
                </label>

                {plainStrokeHidden ? (
                    <p
                        className="path-tool-meta"
                        style={{ margin: "4px 0 0", opacity: 0.7 }}
                    >
                        The preset's stroke, parallel lines, and objects style
                        the outlines (its routing is ignored).
                    </p>
                ) : (
                    <>
                        <div className="field-row field-row--two">
                            <label className="field-label">
                                Stroke{colorMixed ? " (Mixed)" : ""}
                                <input
                                    type="color"
                                    value={
                                        colorMixed
                                            ? "#000000"
                                            : (color as string)
                                    }
                                    onChange={(e) =>
                                        apply({ color: e.target.value })
                                    }
                                />
                            </label>
                            <label className="field-label">
                                Width{widthMixed ? " (Mixed)" : ""}
                                <DebouncedInput
                                    type="number"
                                    min={0.1}
                                    step={0.1}
                                    value={widthMixed ? "" : (width as number)}
                                    placeholder={widthMixed ? "Mixed" : undefined}
                                    onChange={(v) =>
                                        apply({
                                            strokeWidth: Math.max(
                                                0.1,
                                                num(
                                                    v,
                                                    widthMixed
                                                        ? 1
                                                        : (width as number),
                                                ),
                                            ),
                                        })
                                    }
                                />
                            </label>
                        </div>

                        <label className="field-label">
                            Dash (e.g. "4 2", empty = solid)
                            {dashMixed ? " (Mixed)" : ""}
                            <input
                                type="text"
                                value={dashMixed ? "" : (dash as string)}
                                placeholder={dashMixed ? "Mixed" : undefined}
                                onChange={(e) => apply({ dash: e.target.value })}
                            />
                        </label>
                    </>
                )}

                {fillable && (
                    <div style={{ marginTop: 6 }}>
                        {fillMixed ? (
                            <div
                                className="field-row"
                                style={{ gap: 8, alignItems: "center" }}
                            >
                                <span className="path-tool-meta">
                                    Fill: Mixed
                                </span>
                                <button
                                    type="button"
                                    className="generic-button"
                                    style={{ padding: "2px 8px" }}
                                    onClick={() => apply({ fill: null })}
                                    title="Clear fill on all selected shapes"
                                >
                                    Clear fill
                                </button>
                            </div>
                        ) : (
                            <FillControl
                                fill={(fill as Fill | null) ?? null}
                                patterns={doc.patterns}
                                onChange={(f) => apply({ fill: f })}
                                label="Selection"
                            />
                        )}
                    </div>
                )}

                <VisibilityToggle
                    mixed={visibleMixed}
                    visible={visibleMixed ? true : (visible as boolean)}
                    onChange={(v) => apply({ visible: v })}
                />
            </div>

            {/* --- Danger --- */}
            <div className="diagram-node-panel-section">
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => {
                        if (ellSel.length) removeSelectedEllipses();
                        if (linSel.length) removeSelectedLines();
                        if (arcSel.length) removeSelectedArcs();
                        if (rctSel.length) removeSelectedRects();
                        if (polSel.length) removeSelectedPolygons();
                    }}
                    title="Delete all selected shapes"
                >
                    <Trash2 size={14} aria-hidden /> Delete {shapes.length} shapes
                </button>
            </div>
        </div>
    );
}

/**
 * A visibility checkbox that renders the indeterminate (dash) state when the
 * selection's `visible` flags disagree; toggling it from there shows everything.
 */
function VisibilityToggle({
    mixed,
    visible,
    onChange,
}: {
    mixed: boolean;
    visible: boolean;
    onChange: (v: boolean) => void;
}) {
    const ref = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = mixed;
    }, [mixed]);

    return (
        <label
            className="field-label"
            style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
                marginTop: 6,
            }}
        >
            <input
                ref={ref}
                type="checkbox"
                checked={visible}
                onChange={(e) => onChange(e.target.checked)}
            />
            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                }}
            >
                {visible ? (
                    <Eye size={14} aria-hidden />
                ) : (
                    <EyeOff size={14} aria-hidden />
                )}
                Visible{mixed ? " (Mixed)" : ""}
            </span>
        </label>
    );
}
